import { ArrowBack, AttachMoney, FilterList, Group } from '@mui/icons-material'
import { Box, Button, Drawer, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import DatePickerWithArrows from '../../components/DatePickerWithArrows/DatePickerWithArrows'
import FilterProjectContributionMenu, {
  initialProjectContributionFilters,
  ProjectContributionFilterType,
} from '../../components/FilterMenu/Projects/FilterProjectContributionMenu'
import Searchbar from '../../components/Searchbar/Searchbar'
import ProjectDetailsTable from '../../components/Table/ProjectDetails/ProjectDetailsTable'
import { ProjectDetails } from '../../store/redux/reducers/projectsSlice'
import { SquareButton } from '../../styles/ButtonStyles'
import { StyledContainerRowBetween } from '../../styles/ContainerStyles'

type EmployeeContributionDrawerProps = {
  resize: boolean
  fullWidth: boolean
  projectDetails: ProjectDetails
  isEditMode: boolean
  isNotLeaderOfProject: boolean
  selectedDate: dayjs.Dayjs
  handleDateChange: (e: dayjs.Dayjs | null) => void
}

const EmployeeContributionDrawer = ({
  resize,
  fullWidth,
  projectDetails,
  isEditMode,
  isNotLeaderOfProject,
  selectedDate,
  handleDateChange,
}: EmployeeContributionDrawerProps) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleOpen = () => setOpen(true)

  const handleClose = () => setOpen(false)

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isLargeSize = useMediaQuery(theme.breakpoints.down('lg'))
  const [searchInput, setSearchInput] = useState('')
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState<ProjectContributionFilterType>(
    initialProjectContributionFilters
  )

  useEffect(() => {
    if (projectDetails.details.length === 0) {
      setIsFilterMenuOpen(false)
    }
  }, [projectDetails.details])

  return (
    <>
      {resize ? (
        <Tooltip title={t('page.worklog.costs')} placement="bottom">
          <SquareButton onClick={handleOpen} color="secondary" sx={{ display: isNotLeaderOfProject ? 'none' : 'auto' }}>
            <Group />
          </SquareButton>
        </Tooltip>
      ) : (
        <Button
          variant={'contained'}
          startIcon={<AttachMoney />}
          color="secondary"
          size="small"
          onClick={handleOpen}
          fullWidth={fullWidth}
          sx={{ display: isNotLeaderOfProject ? 'none' : 'auto' }}
        >
          {t('page.projects.detailsView.employeeContribution')}
        </Button>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { bgcolor: 'background.main', height: '100%' }, elevation: 0 }}
      >
        <Stack px={isDownSm ? 3 : 6} pt={3} pb={3} sx={{ height: '100%' }}>
          <Button
            onClick={handleClose}
            variant="text"
            color="secondary"
            startIcon={<ArrowBack />}
            size="small"
            sx={{ position: 'absolute', ml: -3 }}
          >
            {t('components.infoSidebar.return')}
          </Button>
          <Stack sx={{ pt: 6, gap: 2, flex: 1, minHeight: 0 }}>
            <StyledContainerRowBetween>
              <Typography variant="h5" color="text.mediumEmphasis">
                {t('page.projects.detailsView.employeeContribution')}
              </Typography>
              <Tooltip
                title={projectDetails.details.length === 0 ? t('page.projects.detailsView.disabledTooltip') : ''}
              >
                <span>
                  <Button
                    disabled={projectDetails.details.length === 0}
                    variant="contained"
                    disableElevation
                    startIcon={<FilterList />}
                    size="small"
                    color="secondary"
                    className={isFilterMenuOpen ? 'selected' : ''}
                    onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                  >
                    {t('page.usersProfile.filterTags')}
                  </Button>
                </span>
              </Tooltip>
            </StyledContainerRowBetween>

            {isLargeSize && (
              <Collapse in={isFilterMenuOpen} orientation={isLargeSize ? 'vertical' : 'horizontal'}>
                <FilterProjectContributionMenu
                  setFilteringOptions={setSelectedFilters}
                  projectDetails={projectDetails}
                />
              </Collapse>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: isFilterMenuOpen ? 2 : 0,
                flex: 1,
                minHeight: 0,
                overflow: isLargeSize ? undefined : 'hidden',
                pb: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  gap: 2,
                  minHeight: 0,
                  overflow: isLargeSize ? undefined : 'auto',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isDownSm ? 'column' : 'row',
                    gap: 2,
                    alignItems: isDownSm ? 'center' : 'flex-start',
                  }}
                >
                  <Searchbar
                    placeholder={t('components.placeholder.search')}
                    handleChange={(value: string) => setSearchInput(value)}
                  />
                  <DatePickerWithArrows
                    disabled={isEditMode || isNotLeaderOfProject}
                    onChange={handleDateChange}
                    value={dayjs(selectedDate)}
                    view="month"
                    views={['month', 'year']}
                    containerSxProps={{ width: isDownSm ? 'fit-content' : '100%' }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        inputProps: { 'aria-label': 'Select a month and year' },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, ...(isLargeSize && { pb: 2 }) }}>
                  <ProjectDetailsTable
                    selectedFilters={selectedFilters}
                    searchInput={searchInput}
                    projectDetails={projectDetails}
                    isNotLeaderOfProject={isNotLeaderOfProject}
                  />
                </Box>
              </Box>
              {!isLargeSize && (
                <Collapse in={isFilterMenuOpen} orientation={isLargeSize ? 'vertical' : 'horizontal'}>
                  <FilterProjectContributionMenu
                    setFilteringOptions={setSelectedFilters}
                    projectDetails={projectDetails}
                  />
                </Collapse>
              )}
            </Box>
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}

export default EmployeeContributionDrawer
